import {ViewTypes} from '@fantasmagames/slot/src/common/view/View';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {Position, ReelConstants, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';

export class ReelsUtils {
	public static getReelCenterPosition(reelIndex: number, viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): Position {
		return {
			x:
				ReelConstants.getReelsOffsets(viewType).x +
				ReelConstants.getSymbolSlot(viewType).width * reelIndex +
				ReelConstants.getSymbolSlot(viewType).width / 2,
			y:
				ReelConstants.getReelsOffsets(viewType).y +
				ReelConstants.getSymbolSlot(viewType).height * (ReelConstants.MAX_ROW_AMOUNT / 2) +
				ReelConstants.getSymbolSlot(viewType).height / 2,
		};
	}

	public static getSymbolScreenPosition(pos: SymbolPosition, viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): Position {
		return {
			x:
				ReelConstants.getReelsOffsets(viewType).x +
				ReelConstants.getSymbolSlot(viewType).width * pos.x +
				ReelConstants.getSymbolSlot(viewType).width / 2,
			y:
				ReelConstants.getReelsOffsets(viewType).y +
				ReelConstants.getSymbolSlot(viewType).height * pos.y +
				ReelConstants.getSymbolSlot(viewType).height / 2,
		};
	}

	public static getPositionOnReels(
		pos: SymbolPosition,
		reels: GameSymbol[][],
		viewType: ViewTypes = ReelConstants.DEFAULT_VIEW,
	): Position {
		const xPos: number =
			ReelConstants.getReelsOffsets(viewType).x +
			pos.x * ReelConstants.getSymbolSlot(viewType).width +
			ReelConstants.getSymbolSlot(viewType).width / 2;
		const ySymSizeOffset: number = this.getSymbolOnReelsPosition(pos, reels, viewType);
		const yPos: number =
			ReelConstants.getReelsOffsets().y +
			ySymSizeOffset * ReelConstants.getSymbolSlot(viewType).height +
			ReelConstants.getSymbolSlot(viewType).height / 2;

		return {x: xPos, y: yPos};
	}

	public static getPositionInTheMiddleOfTheReel(reelIndex: number, viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): Position {
		const xPos: number =
			ReelConstants.getReelsOffsets(viewType).x +
			reelIndex * ReelConstants.getSymbolSlot(viewType).width +
			ReelConstants.getSymbolSlot(viewType).width / 2;
		const yPos: number =
			ReelConstants.getReelsOffsets(viewType).y + (ReelConstants.ROW_AMOUNT / 2) * ReelConstants.getSymbolSlot(viewType).height;

		return {x: xPos, y: yPos};
	}

	public static getSymbolOnReelsPosition(
		pos: SymbolPosition,
		reels: GameSymbol[][],
		viewType: ViewTypes = ReelConstants.DEFAULT_VIEW,
	): number {
		let offset: number = 0;
		for (let i = 0; i < pos.y; i++) {
			const sym: GameSymbol = reels[pos.x][i];
			offset += sym.size.height;
		}

		return offset;
	}

	public static getVectorLengthFromPositions(fromPosition: SymbolPosition, toPosition: SymbolPosition): number {
		const a: number = Math.pow(Math.abs(fromPosition.x - toPosition.x), 2);
		const b: number = Math.pow(Math.abs(fromPosition.y - toPosition.y), 2);

		return Math.sqrt(a + b);
	}

	/**
	 *
	 * Mapping -> [0,1][2,3]
	 * To ->      [0,2,1,3]
	 *
	 * @memberof ReelsUtils
	 */
	public static matrixToArray<T>(reelMatrix: T[][]): T[] {
		const array: T[] = [];
		let totalLength: number = 0;

		for (const col of reelMatrix) {
			totalLength += col.length;
		}

		const indexes: MatrixFormat = {row: -1, column: 0};

		for (let index = 0; index < totalLength; index++) {
			indexes.column++;

			if (index % reelMatrix.length === 0) {
				indexes.row++;
				indexes.column = 0;
			}

			array.push(reelMatrix[indexes.column][indexes.row]);
		}

		return array;
	}

	public static matrixToArraySimple<T>(reelMatrix: T[][]): T[] {
		const array: T[] = [];

		for (let col = 0; col < reelMatrix.length; col++) {
			const column = reelMatrix[col];
			for (let row = 0; row < column.length; row++) {
				const sym = column[row];
				array.push(sym);
			}
		}

		return array;
	}

	public static MatrixToArraySortingOrderByColumn<T>(reelMatrix: T[][], indexes: MatrixFormat, index: number): void {
		indexes.column++;

		if (index % reelMatrix.length === 0) {
			indexes.row++;
			indexes.column = 0;
		}
	}

	/**
	 * Remaps an Array to a Matrix based on set columns.
	 * Mapping is -> [0,1,2,3]
	 * To ->        [0,2][1,3]
	 *
	 */
	public static arrayToMatrix<T>(array: T[], columns: number): T[][] {
		const matrix: T[][] = [];

		let columnIndex: number = 0;

		for (let index = 0; index < array.length; index++) {
			if (!matrix[columnIndex]) {
				matrix.push([]);
			}

			matrix[columnIndex].push(array[index]);

			columnIndex++;

			if (columnIndex > columns - 1) {
				columnIndex = 0;
			}
		}

		return matrix;
	}

	/**
	 *    Remaps an Array to a Matrix based on set columns and runs a provided callback function on each item.
	 *    Mapping is -> [0,1,2,3]
	 *  To ->         [0,2][1,3]
	 *
	 * @memberof ReelsUtils
	 */
	public static arrayToMatrixWithCallback<I, O>(
		array: I[],
		columns: number,
		rows: number,
		itemCallback: MatrixItemCallback<I, O>,
		orderLogic: Function = ReelsUtils.MatrixSortingOrderByRow,
		pushLogic: MatrixPushLogic<O> = ReelsUtils.IsNotNullPushMatrixColumn,
	): O[][] {
		const matrix: O[][] = [];

		const indexes: MatrixFormat = {row: 0, column: 0};
		const limits: MatrixLimitsFormat = {columns, rows};

		for (let index = 0; index < array.length; index++) {
			const item: O = itemCallback(array[index], indexes, limits, index);

			pushLogic(matrix, item, indexes, index);
			orderLogic(matrix, indexes, limits, index);
		}

		return matrix;
	}

	public static AlwaysPushMatrixColumn<O>(matrix: O[][], item: O, indexes: MatrixFormat, index: number): void {
		if (!matrix[indexes.column]) {
			matrix.push([]);
		}

		matrix[indexes.column].push(item);
	}

	public static IsNotNullPushMatrixColumn<O>(matrix: O[][], item: O, indexes: MatrixFormat, index: number): void {
		if (!matrix[indexes.column]) {
			matrix.push([]);
		}

		if (item) {
			matrix[indexes.column].push(item);
		}
	}

	public static MatrixSortingOrderByRow<O>(matrix: O[][], indexes: MatrixFormat, limits: MatrixLimitsFormat, index: number): void {
		indexes.column++;

		if (indexes.column > limits.columns - 1) {
			indexes.row++;
			indexes.column = 0;
		}
	}

	public static MatrixSortingOrderByColumn<O>(matrix: O[][], indexes: MatrixFormat, limits: MatrixLimitsFormat, index: number): void {
		// TODO: Fix.
	}

	public static MatrixSortingOrderByRowReverse<O>(matrix: O[][], indexes: MatrixFormat, limits: MatrixLimitsFormat, index: number): void {
		// TODO: Fix.
	}

	public static MatrixSortingOrderByColumnReverse<O>(
		matrix: O[][],
		indexes: MatrixFormat,
		limits: MatrixLimitsFormat,
		index: number,
	): void {
		// TODO: Fix.
	}

	public static MatrixSortingOrderRandom<O>(matrix: O[][], indexes: MatrixFormat, limits: MatrixLimitsFormat, index: number): void {
		// TODO: Fix.
	}
}

export interface MatrixPushLogic<O> {
	(matrix: O[][], newItem: O, indexes: MatrixFormat, index: number): void;
}

export interface MatrixFormat {
	row: number;
	column: number;
}

export interface MatrixLimitsFormat {
	rows: number;
	columns: number;
}

interface MatrixItemCallback<I, O> {
	(arrayItem: I, indexes: MatrixFormat, limits: MatrixLimitsFormat, index: number): O;
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/reels/ReelsUtils.ts